// @ts-nocheck
/**
 * Global support alerts for agents (Jhony, 17/08/2026).
 *
 * The toast stack was born inside the Inbox screen, so it only alerted agents
 * who were already looking at the Inbox. This component lives in the admin
 * Layout instead: a handoff / user reply now reaches the agent on ANY screen
 * (dashboard included), via socket `support_notify` (emitted to the agent's
 * personal room — no inbox room join needed) plus the on-entry recompute
 * (GET /support/my-alerts) when the layout mounts.
 *
 * Clicking a toast navigates to the Inbox with ?ticket=<id> (the Inbox reads
 * the param and opens the conversation). When a ticket is opened by ANY means,
 * the Inbox fires the `support:ticket-opened` window event and the matching
 * toast clears here. Sound preference is the Inbox bell checkbox (localStorage
 * `supportInboxSound`), read live at event time; client-opened tickets always
 * beep (the extra "shout"). Renders nothing for non-agents.
 */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box, Chip, Paper, Typography } from "@mui/material";
import { socket } from "../../services/socket.io/socket.io.config";
import { fetchSupportCategories, fetchSupportMyAlerts } from "../../api/support";
import { isStalledWait, waitingLabel } from "../../utils/methods/supportTicketStatus";

function categoryName(categories, key) {
  if (!key) return "General";
  const c = (categories || []).find((x) => x.key === key);
  return c ? c.name : key;
}

// Short WebAudio beep — no asset needed. Client tickets get a 3-note sequence.
function beep(fromClient) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const seq = fromClient ? [880, 660, 880] : [660];
    seq.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.frequency.value = f;
      g.gain.value = 0.06;
      o.start(ctx.currentTime + i * 0.18);
      o.stop(ctx.currentTime + i * 0.18 + 0.14);
    });
  } catch {
    /* audio unavailable (autoplay policy) — the toast still shows */
  }
}

export default function SupportAgentAlerts() {
  const { isSupportAgent } = useSelector((state) => state.auth);
  const [toasts, setToasts] = useState([]);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isSupportAgent) return undefined;
    // Entry seeding: what needs THIS agent's attention right now — the live
    // socket only covers whoever was online when it happened.
    (async () => {
      const [c, a] = await Promise.all([fetchSupportCategories(), fetchSupportMyAlerts()]);
      setCategories(c?.response?.data?.data ?? []);
      const alerts = a?.response?.data?.data ?? [];
      if (alerts.length) {
        setToasts((prev) => {
          const seen = new Set(prev.map((t) => t.ticketId));
          const seeded = alerts
            .filter((al) => !seen.has(al.ticketId))
            .map((al) => ({ id: `${al.ticketId}-entry`, ...al }));
          return [...prev, ...seeded].slice(0, 6);
        });
      }
    })();

    const onNotify = (p) => {
      if (!p?.ticketId) return;
      setToasts((prev) =>
        [{ id: `${p.ticketId}-${Date.now()}`, ...p }, ...prev.filter((t) => t.ticketId !== p.ticketId)].slice(0, 6),
      );
      if (p.fromClient || localStorage.getItem("supportInboxSound") === "1") beep(p.fromClient);
    };
    const onOpened = (e) => {
      const id = e?.detail?.ticketId;
      if (id) setToasts((prev) => prev.filter((t) => String(t.ticketId) !== String(id)));
    };
    socket.on("support_notify", onNotify);
    window.addEventListener("support:ticket-opened", onOpened);
    return () => {
      socket.off("support_notify", onNotify);
      window.removeEventListener("support:ticket-opened", onOpened);
    };
  }, [isSupportAgent]);

  // The card stays on screen while the wait grows, so re-render every minute to
  // keep "waiting 2h 5m" honest (and to flip a fresh card to "Waiting on you").
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!toasts.length) return undefined;
    const iv = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(iv);
  }, [toasts.length]);

  // Blink the tab title while there are unseen alerts — works in background tabs.
  useEffect(() => {
    if (!toasts.length) return undefined;
    const original = document.title;
    let flip = false;
    const iv = setInterval(() => {
      flip = !flip;
      document.title = flip ? `(${toasts.length}) Support!` : original;
    }, 900);
    return () => {
      clearInterval(iv);
      document.title = original;
    };
  }, [toasts.length]);

  if (!isSupportAgent || !toasts.length) return null;

  return (
    <Box sx={{ position: "fixed", top: 128, right: 24, zIndex: 2000, display: "flex", flexDirection: "column", gap: 1, width: 330 }}>
      {toasts.length > 1 ? (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Chip
            size="small"
            label={`Close all (${toasts.length})`}
            onClick={() => setToasts([])}
            sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "#fff", cursor: "pointer", "&:hover": { bgcolor: "rgba(255,255,255,0.28)" } }}
          />
        </Box>
      ) : null}
      {toasts.map((t) => {
        // Stalled = the wait crossed the reminder window. The wording switches
        // from reporting an event ("User replied") to naming the debt ("Waiting
        // on you"), because by then the agent has already seen the first card.
        const stalled = t.kind !== "handoff" && isStalledWait(t.waitingSince, now);
        const waited = waitingLabel(t.waitingSince, now);
        const urgent = t.fromClient || stalled;
        return (
        <Paper
          key={t.id}
          onClick={() => {
            setToasts((prev) => prev.filter((x) => x.id !== t.id));
            navigate(`/support-inbox?ticket=${encodeURIComponent(String(t.ticketId))}`);
          }}
          sx={{
            p: 1.25,
            cursor: "pointer",
            bgcolor: "#221739",
            color: "#fff",
            border: urgent ? "2px solid #E56B6B" : "1px solid rgba(232,163,61,0.7)",
            boxShadow: urgent ? "0 0 14px rgba(229,107,107,0.45)" : "0 4px 10px rgba(0,0,0,0.4)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
              {t.kind === "handoff" ? "Needs a human" : stalled ? "Waiting on you" : "User replied"} — #
              {t.ticketNumber}
            </Typography>
            {t.fromClient ? (
              <Chip size="small" label="CLIENT" sx={{ bgcolor: "#E56B6B", color: "#fff", height: 18, fontSize: 10, fontWeight: 700 }} />
            ) : null}
            <Typography
              onClick={(e) => {
                e.stopPropagation();
                setToasts((prev) => prev.filter((x) => x.id !== t.id));
              }}
              sx={{ fontSize: 14, opacity: 0.6, px: 0.5, "&:hover": { opacity: 1 } }}
            >
              ✕
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
            {categoryName(categories, t.category)}
            {waited ? ` · waiting ${waited}` : ""}
            {t.reason ? ` — ${t.reason}` : ""}
          </Typography>
        </Paper>
        );
      })}
    </Box>
  );
}
